export type HeaderNavLink = {
  id: string
  label: string
  href: string
  ariaLabel: string
  target?: '_blank' | '_parent'
  colorClasses?: {
    active: string
    inactive: string
    hover?: string
  }
  mobileColorClasses?: {
    active: string
    inactive: string
  }
}

export type HeaderCTAButton = {
  id: string
  label: string
  href: string
  ariaLabel: string
  target?: '_blank' | '_parent'
  desktopClasses: string
  mobileClasses: string
}

export const HeaderNavLinks: HeaderNavLink[] = [
  {
    id: 'home',
    label: 'Home',
    href: '/',
    ariaLabel: 'Navigate to Home',
    target: '_parent',
    colorClasses: {
      active: 'text-blue-600 dark:text-blue-400',
      inactive: 'text-slate-700 dark:text-slate-300',
      hover: 'hover:text-blue-600 dark:hover:text-blue-400',
    },
    mobileColorClasses: {
      active:
        'bg-primary-100 text-primary-900 dark:bg-primary-900 dark:text-primary-100',
      inactive:
        'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
    },
  },
  {
    id: 'docs',
    label: 'Docs',
    href: '/docs/',
    ariaLabel: 'Docs',
    target: '_parent',
    colorClasses: {
      active: 'text-blue-600 dark:text-blue-400',
      inactive: 'text-slate-700 dark:text-slate-300',
      hover: 'hover:text-blue-600 dark:hover:text-blue-400',
    },
    mobileColorClasses: {
      active:
        'bg-primary-100 text-primary-900 dark:bg-primary-900 dark:text-primary-100',
      inactive:
        'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
    },
  },
  {
    id: 'contact',
    label: 'Contact',
    href: 'https://www.imandra.ai/contact',
    ariaLabel: 'Contact us',
    target: '_blank',
    colorClasses: {
      active: 'text-blue-600 dark:text-blue-400',
      inactive: 'text-slate-700 dark:text-slate-300',
      hover: 'hover:text-blue-600 dark:hover:text-blue-400',
    },
    mobileColorClasses: {
      active:
        'bg-primary-100 text-primary-900 dark:bg-primary-900 dark:text-primary-100',
      inactive:
        'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
    },
  },
]

export const HeaderCTAButtons: HeaderCTAButton[] = [
  {
    id: 'login',
    label: 'Login',
    href: 'https://universe.imandra.ai/login?return_to=%2Fuser%2Fhome',
    ariaLabel: 'Login to Imandra Universe',
    target: '_blank',
    desktopClasses: 'bg-teal-700 text-white hover:bg-teal-600',
    mobileClasses:
      'bg-teal-700 text-white shadow-sm hover:bg-teal-600 hover:shadow-md',
  },
  {
    id: 'signup',
    label: 'Sign up',
    href: 'https://universe.imandra.ai/sign-up',
    ariaLabel: 'Sign up for Imandra Universe',
    target: '_blank',
    desktopClasses:
      'bg-linear-to-r from-teal-600 to-blue-600 text-nowrap text-white hover:from-emerald-500 hover:to-blue-500',
    mobileClasses:
      'bg-linear-to-r from-teal-600 to-blue-600 text-white shadow-sm hover:from-emerald-500 hover:to-blue-500 hover:shadow-md',
  },
]

/** Canonical Imandra Universe auth entry points — single source of truth for
 *  hero CTAs, header CTA buttons, footer links, etc. Direct property access
 *  (`NavigationData.signup.href`) is preferred over `.find()` calls so that
 *  consumers stay terse. See CONVENTIONS.md → "Extend existing data objects". */
export const NavigationData = {
  login: {
    name: 'Login',
    href: 'https://universe.imandra.ai/login?return_to=%2Fuser%2Fhome',
    'aria-label': 'Login to Imandra Universe',
  },
  signup: {
    name: 'Sign up',
    heroLabel: 'Start Now',
    href: 'https://universe.imandra.ai/sign-up',
    'aria-label': 'Sign up for Imandra Universe',
  },
} as const

export type FooterWWWLink = {
  id: string
  label: string
  href: string
  ariaLabel: string
  target?: '_blank' | '_parent'
}

/** Minimal grid targets — canonical www / product URLs used across marketing sites. */
export const FooterWWWLinks: FooterWWWLink[] = [
  {
    id: 'imandra',
    label: 'Imandra',
    href: 'https://www.imandra.ai',
    ariaLabel: 'Visit imandra.ai',
    target: '_blank',
  },
  {
    id: 'universe',
    label: 'Universe',
    href: 'https://universe.imandra.ai',
    ariaLabel: 'Visit Imandra Universe',
    target: '_blank',
  },
  {
    id: 'imandrax',
    label: 'ImandraX',
    href: 'https://imandrax.dev',
    ariaLabel: 'Visit ImandraX',
    target: '_blank',
  },
  {
    id: 'formal-verification',
    label: 'Formal verification',
    href: 'https://www.imandra.ai/formal-verification',
    ariaLabel: 'Formal verification on imandra.ai',
    target: '_blank',
  },
  {
    id: 'contact',
    label: 'Contact',
    href: 'https://www.imandra.ai/contact',
    ariaLabel: 'Contact Imandra',
    target: '_blank',
  },
  {
    id: 'legal',
    label: 'Legal',
    href: 'https://www.imandra.ai/legal',
    ariaLabel: 'Legal information',
    target: '_blank',
  },
]

export const DataFooterLinks = {
  links: {
    title: 'Quick Links',
    items: [
      {
        href: '/',
        'aria-label': 'Navigate to Home',
        text: 'Home',
      },
      {
        href: '/docs/',
        'aria-label': 'Navigate to Documentation',
        text: 'Documentation',
      },
      {
        href: 'https://www.imandra.ai/contact',
        'aria-label': 'Contact us',
        text: 'Contact',
      },
      //   {
      //     href: '/about/',
      //     'aria-label': 'Navigate to About',
      //     text: 'About',
      //   },
    ],
  },
  connect: {
    title: 'Connect',
    items: [
      {
        href: 'https://github.com/imandra-ai',
        'aria-label': 'Visit Imandra on GitHub',
        text: 'GitHub',
      },
      {
        href: 'https://twitter.com/imandra_ai',
        'aria-label': 'Visit Imandra on X (Twitter)',
        text: 'X',
      },
      {
        href: 'https://linkedin.com/company/imandra',
        'aria-label': 'Visit Imandra on LinkedIn',
        text: 'LinkedIn',
      },
    ],
  },
}
